import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MapPin } from 'lucide-react';
import { AUSTRIAN_STATES, getDistrictsForState, getGemeindenForDistrict } from '../../data/austrianLocations';

/**
 * Wohnort fields: Bundesland → Bezirk (residenceCity) → Gemeinde + optional Straße.
 *
 * Schema fields used: residenceState, residenceCity, residenceGemeinde, residenceAddress
 *
 * Props:
 *   value: { residenceState, residenceCity, residenceGemeinde, residenceAddress }
 *   onChange(patch): receives partial form patch object
 *   testIdPrefix: defaults to "residence"
 *   compact: smaller layout (StudentDashboard) vs. spacious (OnboardingWizard)
 *   showAddress: whether to render the optional address input (default true)
 *   showHeading: whether to render the section heading (default false)
 *   required: shows asterisk on the heading
 */
export const ResidenceLocationFields = ({
  value,
  onChange,
  testIdPrefix = 'residence',
  compact = false,
  showAddress = true,
  showHeading = false,
  required = false,
}) => {
  const {
    residenceState = '',
    residenceCity = '',
    residenceGemeinde = '',
    residenceAddress = '',
  } = value || {};

  const triggerCls = compact ? '' : 'h-12 rounded-xl';
  const gridCls = compact ? 'grid grid-cols-1 sm:grid-cols-2 gap-2' : 'grid grid-cols-1 md:grid-cols-2 gap-4';

  return (
    <div className="space-y-2">
      {showHeading && (
        <Label className={compact ? 'flex items-center gap-2' : 'text-base font-semibold flex items-center gap-2'}>
          <MapPin className="w-4 h-4 text-primary" />
          {compact ? 'Wohnort' : `Dein Wohnort${required ? ' *' : ''}`}
        </Label>
      )}

      <div className={gridCls}>
        <div className="space-y-2">
          {!compact && <Label className="text-sm text-slate-600">Bundesland</Label>}
          <Select
            value={residenceState || undefined}
            onValueChange={(v) =>
              onChange({ residenceState: v, residenceCity: '', residenceGemeinde: '' })
            }
          >
            <SelectTrigger className={triggerCls} data-testid={`${testIdPrefix}-state-select`}>
              <SelectValue placeholder="Bundesland wählen" />
            </SelectTrigger>
            <SelectContent className="z-[100]">
              {AUSTRIAN_STATES.map((state) => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          {!compact && <Label className="text-sm text-slate-600">Bezirk</Label>}
          <Select
            value={residenceCity || undefined}
            onValueChange={(v) => onChange({ residenceCity: v, residenceGemeinde: '' })}
            disabled={!residenceState}
          >
            <SelectTrigger className={triggerCls} data-testid={`${testIdPrefix}-district-select`}>
              <SelectValue placeholder={residenceState ? 'Bezirk wählen' : 'Erst Bundesland'} />
            </SelectTrigger>
            <SelectContent className="z-[100]">
              {getDistrictsForState(residenceState).map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {residenceCity && getGemeindenForDistrict(residenceCity).length > 0 && (
          <div className="space-y-2">
            {!compact && <Label className="text-sm text-slate-600">Gemeinde (optional)</Label>}
            <Select
              value={residenceGemeinde || '__none__'}
              onValueChange={(v) =>
                onChange({ residenceGemeinde: v === '__none__' ? '' : v })
              }
            >
              <SelectTrigger className={triggerCls} data-testid={`${testIdPrefix}-gemeinde-select`}>
                <SelectValue placeholder="Gemeinde wählen" />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                <SelectItem value="__none__">-- Keine Auswahl --</SelectItem>
                {getGemeindenForDistrict(residenceCity).map((g) => (
                  <SelectItem key={g.name} value={g.name}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {showAddress && (
        <div className={compact ? '' : 'space-y-2'}>
          {!compact && <Label className="text-sm text-slate-600">Straße / Hausnummer (optional)</Label>}
          <Input
            value={residenceAddress}
            onChange={(e) => onChange({ residenceAddress: e.target.value })}
            placeholder={compact ? 'Straße / Hausnummer (optional)' : 'z.B. Mariahilfer Straße 50'}
            className={compact ? 'mt-2' : 'h-12 rounded-xl'}
            data-testid={`${testIdPrefix}-address-input`}
          />
          {!compact && (
            <p className="text-xs text-slate-400">Hilft uns, Jobs in deiner Nähe zu finden</p>
          )}
        </div>
      )}
    </div>
  );
};
